/**
 * A chamada na tela — toque e conversa.
 *
 * **Montado acima do outlet do router**, de propósito: navegar no Desk não
 * pode desmontar isto, senão a conexão WebRTC cai no meio da ligação. É o erro
 * mais comum de interface de chamada.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Mic, MicOff, Phone, PhoneOff } from "lucide-react";
import { useLiveKitCall } from "@/hooks/useLiveKitCall";

function duracao(desde: number | null): string {
  if (!desde) return "00:00";
  const s = Math.floor((Date.now() - desde) / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

export function CallDock() {
  const { t } = useTranslation();
  const chamada = useLiveKitCall();
  const [, forcarRelogio] = useState(0);

  // O cronômetro só existe enquanto há conversa — nada de timer rodando à toa.
  useEffect(() => {
    if (chamada.situacao !== "em_chamada") return;
    const id = setInterval(() => forcarRelogio((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [chamada.situacao]);

  if (chamada.situacao === "ociosa") return null;

  const tocando = chamada.situacao === "tocando";
  const conectando = chamada.situacao === "conectando";
  const recebida = chamada.recebida;
  const quem =
    recebida?.contactName || recebida?.fromNumber || t("voice.unknownCaller");

  return (
    <div
      role="dialog"
      aria-live="assertive"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-gray-200 bg-white px-4 py-3 shadow-2xl sm:inset-x-auto sm:bottom-4 sm:right-4 sm:w-80 sm:rounded-xl sm:border dark:border-gray-700 dark:bg-gray-800"
      style={{ paddingBottom: "max(0.75rem, var(--safe-bottom))" }}
    >
      <div className="flex items-center gap-3">
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            tocando
              ? "animate-pulse bg-green-100 text-green-700 dark:bg-green-900/40"
              : "bg-primary-100 text-primary-700 dark:bg-primary-900/40"
          }`}
        >
          <Phone className="h-4 w-4" />
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
            {quem}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {tocando
              ? t("voice.incoming")
              : conectando
                ? t("voice.connecting")
                : duracao(chamada.inicio)}
          </p>
        </div>
      </div>

      {chamada.erro && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">{chamada.erro}</p>
      )}

      <div className="mt-3 flex items-center gap-2">
        {tocando && recebida && (
          <>
            <button
              type="button"
              onClick={() => void chamada.atender(recebida.callId)}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
            >
              <Phone className="h-4 w-4" />
              {t("voice.answer")}
            </button>
            <button
              type="button"
              onClick={() => void chamada.recusar(recebida.callId)}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200"
            >
              <PhoneOff className="h-4 w-4" />
              {t("voice.decline")}
            </button>
          </>
        )}

        {chamada.situacao === "em_chamada" && (
          <>
            <button
              type="button"
              onClick={() => void chamada.alternarMudo()}
              aria-pressed={chamada.mudo}
              className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm ${
                chamada.mudo
                  ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                  : "border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-200"
              }`}
            >
              {chamada.mudo ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              {chamada.mudo ? t("voice.unmute") : t("voice.mute")}
            </button>
            <button
              type="button"
              onClick={() => void chamada.desligar()}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              <PhoneOff className="h-4 w-4" />
              {t("voice.hangUp")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default CallDock;
